import React, { useEffect, useState } from "react";
import { getBadges } from "@/lib/api";
import { Loader2, Trophy } from "lucide-react";
import PageLoader from "@/components/wishire/PageLoader";

export default function BadgesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBadges().then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader text="Loading achievements…" />;

  const badges = data?.badges || [];
  const earned = badges.filter(b => b.earned);
  const locked = badges.filter(b => !b.earned);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10">
        <span className="overline">Achievements</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          {data?.earned_count} of {data?.total_count}{" "}
          <span className="italic text-stone-500">badges earned.</span>
        </h1>
        <p className="mt-3 text-stone-500">Complete platform milestones to earn all badges.</p>
      </div>

      {/* Progress bar */}
      <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 wsh-grain mb-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{background:"radial-gradient(400px 250px at 80% 20%, rgba(217,119,6,0.5), transparent 60%)"}}/>
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <Trophy size={24} className="text-amber-300"/>
            <p className="font-display text-2xl">{data?.earned_count} / {data?.total_count} Achievements</p>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden max-w-md">
            <div className="h-full rounded-full bg-amber-300 transition-all duration-700"
              style={{width:`${Math.round(((data?.earned_count||0)/(data?.total_count||1))*100)}%`}}/>
          </div>
          <p className="mt-2 text-sm text-stone-400">{Math.round(((data?.earned_count||0)/(data?.total_count||1))*100)}% complete</p>
        </div>
      </div>

      {/* Earned */}
      {earned.length > 0 && (
        <div className="mb-8">
          <p className="overline mb-4">Earned</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {earned.map(b => <BadgeCard key={b.id} badge={b}/>)}
          </div>
        </div>
      )}

      {/* Locked */}
      {locked.length > 0 && (
        <div>
          <p className="overline mb-4">Locked</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {locked.map(b => <BadgeCard key={b.id} badge={b} locked/>)}
          </div>
        </div>
      )}
    </div>
  );
}

function BadgeCard({ badge, locked }) {
  return (
    <div className={`rounded-2xl border p-5 text-center transition-all ${locked ? "border-stone-100 bg-stone-50 opacity-50" : "border-amber-200 bg-amber-50 shadow-sm"}`}>
      <div className="text-3xl mb-3">{locked ? "🔒" : badge.icon}</div>
      <p className={`font-display text-sm ${locked ? "text-stone-400" : "text-stone-800"}`}>{badge.name}</p>
      <p className="text-xs text-stone-500 mt-1 leading-relaxed">{badge.description}</p>
    </div>
  );
}
